import React, { useState, useEffect, useCallback } from 'react';
import DaumPostcodeEmbed from 'react-daum-postcode';
import styled from 'styled-components';
import { palette } from 'pages/AnyfitMall/theme';

// 상수 정의
const POSTCODE_BUTTON_WIDTH = 100;
const POSTCODE_BUTTON_HEIGHT = 40;
// 카카오 우편번호 iframe 높이 클램프 범위
// - MIN: 검색 전 기본 높이 보장 (카카오 권장 400 이상)
// - MAX: Z Flip 펼침(880px) 등 길쭉한 화면에서 모달이 화면 밖으로 밀리지 않도록 dvh로 제한
const EMBED_MIN_HEIGHT = 400;
const EMBED_MAX_HEIGHT_DVH = 75;

// 스타일 상수
const styles = {
  button: {
    maxWidth: `${POSTCODE_BUTTON_WIDTH}px`,
    minWidth: `${POSTCODE_BUTTON_WIDTH}px`,
    width: '20%',
    color: palette.AnyBlue,
    backgroundColor: palette.AnyBlue100,
    marginLeft: '12px',
    border: `1px solid ${palette.AnyBlue100}`,
    borderRadius: '8px',
    fontSize: '16px',
    boxShadow: 'none',
    marginBottom: '12px',
    marginTop: '12px',
    height: `${POSTCODE_BUTTON_HEIGHT}px`,
    minHeight: `${POSTCODE_BUTTON_HEIGHT}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'filter 0.3s ease',
  },
  error: {
    color: '#dc3545',
    marginTop: '8px',
    fontSize: '14px',
  },
  wrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100dvh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    overflow: 'hidden',
    padding: 'clamp(0px, 2vw, 16px)',
  },
  content: {
    width: 'min(500px, calc(100vw - 16px))',
    maxHeight: '90dvh',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  embed: {
    width: '100%',
    border: 'none',
  },
};

const PostCodeButton = styled.button`
  ${styles.button}
  &:hover {
    filter: brightness(90%);
  }
`;

const ErrorMessage = styled.div`
  ${styles.error}
`;

const PostcodeWrapper = styled.div`
  ${styles.wrapper}
`;

const PostcodeContent = styled.div`
  ${styles.content}
`;

const DaumPostcode = ({ handleFormDataChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [embedHeight, setEmbedHeight] = useState(EMBED_MIN_HEIGHT);

  const handleResize = useCallback((size) => {
    const maxPx = window.innerHeight * (EMBED_MAX_HEIGHT_DVH / 100);
    const clamped = Math.min(Math.max(size.height, EMBED_MIN_HEIGHT), maxPx);
    setEmbedHeight(clamped);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleComplete = useCallback(
    (data) => {
      try {
        let fullAddress = data.address;
        let extraAddress = '';

        if (data.addressType === 'R') {
          if (data.bname !== '') {
            extraAddress += data.bname;
          }
          if (data.buildingName !== '') {
            extraAddress +=
              extraAddress !== ''
                ? `, ${data.buildingName}`
                : data.buildingName;
          }
          fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
        }

        handleFormDataChange('fullAddress', fullAddress);
        handleFormDataChange('zoneCode', data.zonecode);
        setError(null);
        setIsOpen(false);
      } catch (err) {
        setError('주소를 처리하는 중 오류가 발생했습니다.');
        console.error('주소 처리 오류:', err);
      }
    },
    [handleFormDataChange],
  );

  const handleClick = useCallback(() => {
    setIsOpen(!isOpen);
    setError(null);
  }, [isOpen]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  return (
    <div className="daum-postcode-container">
      <PostCodeButton onClick={handleClick} aria-label="주소 검색">
        주소찾기
      </PostCodeButton>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      {isOpen && (
        <PostcodeWrapper
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="주소 검색"
        >
          <PostcodeContent>
            <DaumPostcodeEmbed
              onComplete={handleComplete}
              onClose={() => setIsOpen(false)}
              onResize={handleResize}
              onSearch={(data) => {
                if (!data) {
                  setError('주소를 찾을 수 없습니다.');
                }
              }}
              autoResize
              minWidth={300}
              style={{ ...styles.embed, height: `${embedHeight}px` }}
            />
          </PostcodeContent>
        </PostcodeWrapper>
      )}
    </div>
  );
};

export default DaumPostcode;
